import { ApolloError } from '@apollo/client';
import { GraphQLError } from 'graphql';

import { getClient } from '@/api/config';
import { EFFECTIVE_CONSENT_PREFERENCE } from '@/api/consent_management/effective_consent_preference';
import { RECORD_CONSENT_PREFERENCE_EVENT } from '@/api/consent_management/record_consent_preference_event';
import type { EffectiveConsentPreferenceQuery } from '@/api/gql/graphql';
import { ConsentPreferenceValue } from '@/api/gql/graphql';
import {
  Airgap,
  AirgapConsent,
  AirgapConsentChangeEvent,
  AirgapPurposes,
} from '@/components/app/consent_management/airgap.types';
import {
  CONSENT_CHANGE_EVENT,
  createInteractionEventSource,
  InteractionEventSource,
  whenAirgapReady,
} from '@/components/app/consent_management/airgap-client';
import {
  preferenceSignature,
  toAirgapPurposes,
  toPreferenceInput,
} from '@/components/app/consent_management/consent-preferences';
import notify from '@/helpers/airbrake';
import { getMeta } from '@/helpers/get_meta';

const SESSION_TOKEN_META = 'api-session-token';
const UNAUTHORIZED_CODE = 401;

const RECORDED_PREFERENCE_KEY = 'fooda.consent_sync.recorded_preference';
// Only gates the report below, never the database read: a preference created on another device has
// to be picked up by the next authenticated load.
const RESTORE_SKIP_REPORTED_KEY = 'fooda.consent_sync.restore_skip_reported';
// Enough of the random session token to tell users apart without storing a usable credential.
const SCOPE_LENGTH = 12;

// Airgap silently refuses an interaction it does not trust, so a refused restore waits for the next
// one rather than giving the stored preference up for the rest of the page.
const RESTORE_ATTEMPTS = 3;

type StoredPreference = NonNullable<
  NonNullable<EffectiveConsentPreferenceQuery['data']>['preference']
>;

type SyncContext = {
  airgap: Airgap;
  scope: string;
  restoring: boolean;
  // Held so the user's own answer can release a restore still waiting for an interaction.
  interactions: InteractionEventSource | null;
  // Overlapping submissions are chained so they reach the history in the order they happened and
  // each one sees the recorded-signature marker its predecessor wrote.
  submission: Promise<unknown>;
};

const recordedPreferenceKey = (scope: string) => `${RECORDED_PREFERENCE_KEY}.${scope}`;
const restoreSkipReportedKey = (scope: string) => `${RESTORE_SKIP_REPORTED_KEY}.${scope}`;

// Storage access throws in Safari's private mode, where losing a marker costs one repeat call.
const readStorage = (key: string): string | null => {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
};

const writeStorage = (key: string, value: string): void => {
  try {
    sessionStorage.setItem(key, value);
  } catch {
    // Storage is unavailable; the backend suppresses the duplicate submission instead.
  }
};

const removeStorage = (key: string): void => {
  try {
    sessionStorage.removeItem(key);
  } catch {
    // Nothing stored means nothing to remove.
  }
};

// The session token rotates (SnapPea sessions time out after an hour idle and expire after twelve),
// and each new token means a new scope. Only the current one can be trusted, so the markers left by
// a previous token are dropped instead of piling up for the life of the tab.
const pruneForeignScopes = (scope: string): void => {
  try {
    Object.keys(sessionStorage)
      .filter(
        (key) =>
          key.startsWith(RECORDED_PREFERENCE_KEY) || key.startsWith(RESTORE_SKIP_REPORTED_KEY)
      )
      .filter((key) => !key.endsWith(`.${scope}`))
      .forEach((key) => sessionStorage.removeItem(key));
  } catch {
    // Storage is unavailable, so there is nothing stored to prune.
  }
};

const fetchStoredPreference = async (): Promise<StoredPreference | null> => {
  const { data } = await getClient().query({
    query: EFFECTIVE_CONSENT_PREFERENCE,
    fetchPolicy: 'no-cache',
    // The client defaults to errorPolicy "all", which would report a failed read as an absent
    // preference and skip the restore.
    errorPolicy: 'none',
  });

  return data?.data?.preference ?? null;
};

// The errorHandler link turns a payload error into a GraphQLError, which Apollo wraps as networkError.
const isSessionGone = (error: unknown): boolean =>
  error instanceof ApolloError &&
  error.networkError instanceof GraphQLError &&
  error.networkError.extensions.code === UNAUTHORIZED_CODE;

// A 401 means the session ended while the page stayed open. The choice was not marked as recorded,
// so the next authenticated load sends it again; there is nothing to report.
const reportFailure = (error: unknown): void => {
  if (isSessionGone(error)) return;
  notify({ error });
};

const writeRecordedSignature = (context: SyncContext, signature: string): void => {
  writeStorage(recordedPreferenceKey(context.scope), signature);
};

const submitConsent = async (
  context: SyncContext,
  consent: AirgapConsent,
  signals?: Iterable<string> | null
): Promise<void> => {
  const preferences = toPreferenceInput(consent.purposes);
  // The backend rejects a snapshot with nothing the user can configure.
  if (!preferences.length) return;

  const signature = preferenceSignature(preferences);
  // The backend also suppresses a snapshot matching the current effective row; skipping here just
  // spares it a row lock per page load. The marker knows only what this tab recorded, so a choice
  // repeated after another device changed the history stays unsubmitted until a new session.
  if (readStorage(recordedPreferenceKey(context.scope)) === signature) return;

  await getClient().mutate({
    mutation: RECORD_CONSENT_PREFERENCE_EVENT,
    variables: {
      input: {
        consentGivenAt: consent.timestamp,
        preferences,
        legalRegimes: [...context.airgap.getRegimes()],
        // The backend derives consent_method "implicit" from a GPC or DNT signal, so the list has to
        // carry them. The change event names the signals that caused that change; a state confirmed
        // before this page load has no such event, so the caller reads the ones still active.
        privacySignals: signals ? [...signals] : [],
        airgapVersion: context.airgap.version,
      },
    },
  });

  writeRecordedSignature(context, signature);
  // The history holds a different snapshot now, so a later skip is worth reporting again.
  removeStorage(restoreSkipReportedKey(context.scope));
};

const recordConsent = (
  context: SyncContext,
  consent: AirgapConsent,
  signals?: Iterable<string> | null
): Promise<void> => {
  const submission = context.submission.then(() => submitConsent(context, consent, signals));
  // A failed submission still rejects for its caller but must not wedge the queue.
  context.submission = submission.catch(() => undefined);

  return submission;
};

// Airgap keeps its own inferred value for any purpose left out, and a restore marks the whole
// snapshot confirmed — so anything short of an exact one-to-one match would either promote those
// inferences to an explicit choice or feed setConsent a purpose it no longer knows.
const isFullyRestorable = (
  context: SyncContext,
  preference: StoredPreference,
  purposes: AirgapPurposes
): boolean => {
  const configurable = toPreferenceInput(context.airgap.getConsent().purposes);

  return (
    configurable.length > 0 &&
    configurable.length === preference.preferences.length &&
    configurable.every(({ purpose }) => purpose in purposes)
  );
};

// For a state confirmed before this page load the causal change event is long gone, so the signals
// Airgap still reports are the only evidence left. The backend treats a submitted signal as the cause
// and derives consent_method "implicit" from it, so an active signal is forwarded only when the state
// still looks like the one a signal produces: everything denied. If the user allowed anything they
// overrode the signal, and naming it would file their explicit choice as implicit.
const causalPrivacySignals = (airgap: Airgap, consent: AirgapConsent): string[] => {
  const optedOutOfEverything = toPreferenceInput(consent.purposes).every(
    ({ value }) => value === ConsentPreferenceValue.Denied
  );
  if (!optedOutOfEverything) return [];

  try {
    return [...(airgap.getPrivacySignals?.() ?? [])];
  } catch {
    // An older bundle without the accessor still records the values, only without the signals.
    return [];
  }
};

// Transcend's banner does not react to a programmatic confirm: measured on staging, it keeps asking
// for the rest of the page load even though the restored consent is confirmed. Cosmetic, so a
// missing or failing UI API must never turn a successful restore into a failure.
const hideConsentManager = async (): Promise<void> => {
  try {
    await window.transcend?.hideConsentManager();
  } catch {
    // The preference is restored regardless; the banner stays away from the next load on.
  }
};

const restoreStoredPreference = async (
  context: SyncContext,
  preference: StoredPreference,
  interactions: InteractionEventSource
): Promise<void> => {
  const purposes = toAirgapPurposes(preference.preferences);
  if (!isFullyRestorable(context, preference, purposes)) {
    // A skipped restore re-prompts a user who already answered. A spike here means the stored
    // snapshots no longer match the Transcend purpose configuration. Reported once per session so
    // that a user browsing many pages does not flood Airbrake with the same skip.
    if (!readStorage(restoreSkipReportedKey(context.scope))) {
      writeStorage(restoreSkipReportedKey(context.scope), 'true');
      notify({
        error: new Error('Consent restore skipped: stored preference is not reproducible'),
      });
    }

    return;
  }

  for (let attempt = 0; attempt < RESTORE_ATTEMPTS; attempt += 1) {
    const auth = await interactions.next();
    // The wait was released because the user answered for themselves; there is nothing to restore.
    if (!auth) return;

    // The user answered the banner while we waited, and their choice is the newer one.
    if (context.airgap.getConsent().confirmed) return;

    // The database already holds this state, so it must never be written back, and marking it
    // before the call also disarms the consent-change event the restore itself emits. It waits
    // until the checks above have passed: an answer given while the read was in flight has already
    // recorded its own signature, and overwriting that would suppress the user's next change.
    writeRecordedSignature(context, preferenceSignature(preference.preferences));

    let restored = false;
    context.restoring = true;
    try {
      const options = { confirmed: true, prompted: true, timestamp: preference.consentGivenAt };
      restored = context.airgap.setConsent(auth, purposes, options);
    } finally {
      context.restoring = false;
    }

    if (restored) {
      await hideConsentManager();
      return;
    }
  }
};

const handleConsentChange = async (
  context: SyncContext,
  event: AirgapConsentChangeEvent
): Promise<void> => {
  // Replaying the stored preference is Fooda's action, not a new choice by the user.
  if (context.restoring) return;

  const consent = event.detail?.consent;
  // Transcend's inferred defaults are not a choice and must never reach the consent history.
  if (!consent?.confirmed) return;

  // Their own answer supersedes the stored preference, so a restore need not wait any longer.
  context.interactions?.abort();

  await recordConsent(context, consent, event.detail.signals);
};

const runConsentSync = async (): Promise<void> => {
  // Anonymous visitors are out of scope: Transcend keeps their consent in the browser only.
  const sessionToken = getMeta(SESSION_TOKEN_META);
  if (!sessionToken) return;

  const airgap = await whenAirgapReady();
  if (!airgap) return;

  const context: SyncContext = {
    airgap,
    scope: sessionToken.slice(-SCOPE_LENGTH),
    restoring: false,
    interactions: null,
    submission: Promise.resolve(),
  };

  pruneForeignScopes(context.scope);

  airgap.addEventListener(CONSENT_CHANGE_EVENT, (event) => {
    handleConsentChange(context, event).catch(reportFailure);
  });

  const consent = airgap.getConsent();

  // A confirmed browser choice wins over the stored one, whatever its timestamp says.
  if (consent.confirmed) {
    await recordConsent(context, consent, causalPrivacySignals(airgap, consent));
    return;
  }

  // Armed before the database read, so an interaction during it still authorises the restore.
  const interactions = createInteractionEventSource();
  context.interactions = interactions;
  try {
    const preference = await fetchStoredPreference();
    if (preference) await restoreStoredPreference(context, preference, interactions);
  } finally {
    context.interactions = null;
    interactions.stop();
  }
};

// Reconciles Transcend's browser consent with the signed-in user's stored preference. Safe on every
// authenticated page load: it resolves without acting when there is nothing to sync.
export const startConsentSync = (): void => {
  runConsentSync().catch(reportFailure);
};
